import { Accordion, Button, Grid, Input, Text } from '@afterpaytouch/core'
import type { InputOnChange } from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode, useEffect, useId, useRef, useState } from 'react'

const GridItem = Grid.Item
const GridFixedItem = Grid.FixedItem
import { useUpdateCheckoutPromoMutation } from '@afterpaytouch/portal-api/consumer/checkout'
import { ErrorCode } from '@afterpaytouch/portal-api'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { promoMutationCacheKey, useAppRouter, useCheckoutState, useCheckoutToken } from '../../state'
import { Trans, useTranslation } from '../../utils/convergence'
import { useMoney } from '../Money/useMoney'
import styles from './style.module.css'

const MAX_ATTEMPTS = 5

// Frontend-only sentinel for the pay-later silent-200 response shape (HTTP 200 with
// `promotion: null` and no `errorCode`). Backend never emits this string — the frontend
// synthesises it when an APPLY mutation resolves without an applied promo, so the same
// resolveErrorMessage / view-state / analytics pipeline as real backend rejections can
// drive the user feedback. See the silent-200 branch in handleApply for full context.
const PROMO_SILENT_NO_OP_ERROR_CODE = 'promo_silent_no_op'

type ViewState =
  | { kind: 'collapsed' }
  | { kind: 'expanded'; inputValue: string; errorCode: string | null }
  | { kind: 'applied'; promoCode: string; discountAmount: string }

interface Props {
  testNameSpace?: string
}

function resolveErrorMessage(errorCode: string, t: (key: string) => string): string {
  // Strip incidental whitespace, then compare against the canonical ErrorCode
  // enum values (which already match pay-later's lower_snake_case wire format).
  switch (errorCode.trim()) {
    case ErrorCode.PromoCodeAssignError:
      return t('promoCode:errorInvalid')
    case ErrorCode.AlreadyAssigned:
      return t('promoCode:errorAlreadyUsed')
    case ErrorCode.RateLimited:
      return t('promoCode:tooManyAttempts')
    case PROMO_SILENT_NO_OP_ERROR_CODE:
      return t('promoCode:errorNotApplicable')
    default:
      return t('promoCode:errorGeneric')
  }
}

export const PromoCodeInput: FunctionComponent<Props> = ({ testNameSpace = 'promo-code-input' }) => {
  const { t } = useTranslation()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const checkoutState = useCheckoutState()
  const token = useCheckoutToken()
  const { locale } = useAppRouter()

  const promotion = checkoutState.started?.promotion
  const persistedManualPromo = checkoutState.manualPromoCode
  // Use the checkout's consumer-amount currency as the stable formatter source so non-AU
  // discounts (e.g. USD for US merchants) format correctly even when no promo is applied yet.
  // Fall back to applied-promotion currency in case consumerAmount is unavailable.
  const checkoutCurrency = checkoutState.started?.consumerAmount?.currency
  const promotionCurrency = promotion?.discount?.currency

  const { formatMoney } = useMoney({ locale, currency: checkoutCurrency ?? promotionCurrency, decimalPlaces: 2 })

  // Live decision: should the component hide because the current `started.promotion` was
  // auto-applied at /start (auto-applied takes precedence — the auto-applied one's UI handles
  // its own display)?
  //
  // We disambiguate via `manualPromoCode`, a token-scoped marker recorded in checkout state on
  // every successful manual apply and cleared on remove. The promotion is treated as
  // user-applied iff its code AND the current checkout token both match the marker; otherwise
  // it's auto-applied. The token scope ensures a stale marker from a prior checkout (which can
  // be persisted across token boundaries by redux-persist) can't false-match a new auto-applied
  // /start promo with the same code.
  //
  // This is intentionally NOT a one-shot mount snapshot. If `started.promotion` is later
  // cleared externally (e.g., a reducer like `updatePreferredPayday` recomputes the order and
  // drops the auto-applied promo), the gate must re-open so the user can manually enter a
  // promo. Equally, if the user applies a code via this UI, the dispatch sets the marker so a
  // subsequent render with `started.promotion` populated still resolves to user-applied. The
  // local view state in `useEffect` below tracks the in-flight UI separately.
  const isAutoApplied = ((): boolean => {
    if (!promotion) return false
    if (!persistedManualPromo) return true
    if (persistedManualPromo.token !== token) return true
    return persistedManualPromo.code !== promotion.code
  })()

  // Use a token-scoped `fixedCacheKey` so the summary-row hooks
  // (`useIsCheckoutLoadingOrUpdating`, `useIsDisabledUpdatingCheckout`) reading the
  // same token can subscribe to the same in-flight state and drive their skeletons
  // directly off `isLoading`.
  const [updatePromo, { isLoading, reset: resetPromoMutation }] = useUpdateCheckoutPromoMutation({ fixedCacheKey: promoMutationCacheKey(token) })

  // RTK Query does NOT auto-GC mutation cache entries that use `fixedCacheKey` — they
  // persist until something explicitly calls `reset()`. Without this cleanup, each
  // rotated checkout token would leak its entry into the mutation cache. The trigger
  // side owns the lifecycle, so we reset on token change AND on unmount.
  //
  // Deps `[token]` (intentionally omitting `resetPromoMutation`): on token rotation
  // mid-mount, the previous render's cleanup runs with the previous render's `reset`
  // closure — which dispatches removeMutationResult for the previous fixedCacheKey.
  // Adding `resetPromoMutation` to deps would cycle on every mutation fire (RTK rebuilds
  // `reset` with `[dispatch, fixedCacheKey, promise, requestId]` deps internally), so
  // every Apply / Remove would race-clear its own cache entry. The stale-closure
  // semantics are exactly what we want here.
  useEffect(() => {
    return () => {
      resetPromoMutation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])
  const [attemptCount, setAttemptCount] = useState(0)
  const [liveMessage, setLiveMessage] = useState('')
  const [view, setView] = useState<ViewState>(() => {
    if (promotion) {
      return {
        kind: 'applied',
        promoCode: promotion.code,
        discountAmount: formatMoney(promotion.discount.amount),
      }
    }
    return { kind: 'collapsed' }
  })

  const inputRef = useRef<HTMLInputElement>(null)
  const errorId = useId()
  // Stable id for the Accordion. The core Accordion falls back to a module-level random id
  // when none is passed, so multiple instances on the same page (Summary has several) can
  // collide on the aria-controls attribute. useId is React-instance + SSR stable.
  const accordionId = useId()
  const isAttemptLimitReached = attemptCount >= MAX_ATTEMPTS

  // Reconcile view when checkout-state promotion changes externally (e.g. another reducer like
  // updatePreferredPayday recalculates `started.promotion`). Without this, the local view state
  // can go stale and drive users into invalid actions based on outdated promo status.
  const externalPromoCode = promotion?.code
  const externalDiscountAmount = promotion?.discount?.amount
  useEffect(() => {
    if (externalPromoCode && externalDiscountAmount) {
      setView((prev) => {
        if (prev.kind === 'applied' && prev.promoCode === externalPromoCode) {
          return prev
        }
        return {
          kind: 'applied',
          promoCode: externalPromoCode,
          discountAmount: formatMoney(externalDiscountAmount),
        }
      })
    } else {
      setView((prev) => {
        if (prev.kind === 'applied') {
          return { kind: 'collapsed' }
        }
        return prev
      })
    }
    // formatMoney is intentionally excluded — it's stable for the life of the component
    // and including it can trigger spurious reconciliations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPromoCode, externalDiscountAmount])

  useEffect(() => {
    if (view.kind === 'expanded') {
      inputRef.current?.focus()
    }
  }, [view.kind])

  const handleToggle = (): void => {
    // Toggle is only reachable for the not-yet-applied flow. Once a promo applies, the UI
    // renders a static row outside the Accordion (see render below), so handleToggle has
    // no `applied` branch — the user cannot expand or clear an applied promo. Swapping or
    // removing a promo requires abandoning the checkout and starting fresh.
    if (view.kind === 'collapsed') {
      setView({ kind: 'expanded', inputValue: '', errorCode: null })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'expanded' })
      return
    }
    if (view.kind === 'expanded') {
      setView({ kind: 'collapsed' })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'collapsed' })
    }
  }

  const handleApply = async (): Promise<void> => {
    if (view.kind !== 'expanded') return
    // Defense in depth: the Apply button surfaces a `loading` spinner while a mutation is in
    // flight, but the button stays focusable / Enter-key-reachable. Short-circuit here so a
    // rapid second submit doesn't dispatch a duplicate /update mutation.
    if (isLoading) return
    // Same belt + suspenders for the cap: the button is aria-disabled when the cap is
    // reached, but it remains keyboard-reachable so screen-reader users can focus it.
    if (isAttemptLimitReached) return
    // Same belt + suspenders for the error window: the button is disabled but Enter-key
    // submission via onKeyDown would otherwise bypass it and re-fire the same failing request.
    // handleInputChange clears errorCode on edit, so this only blocks re-submitting the same
    // already-rejected input.
    if (view.errorCode) return
    // Normalize to lowercase before sending to pay-later: ap-promotion-service stores
    // vanity codes lowercase and its runtime check is case-sensitive. Confirmed via the
    // XO-6225 sandbox trace `Runtime condition check: vanity code doesn't match...`.
    const trimmedCode = view.inputValue.trim().toLowerCase()
    if (!trimmedCode) return

    const nextAttempt = attemptCount + 1
    setAttemptCount(nextAttempt)
    logEvent(TrackingEvent.PRESSED_PROMO_CODE_APPLY, { attemptNumber: nextAttempt })

    try {
      const result = await updatePromo({ token, request: { action: 'APPLY', promoCode: trimmedCode } }).unwrap()
      const applied = result.promotion
      if (applied) {
        const discountAmount = formatMoney(applied.discount.amount)
        logEvent(TrackingEvent.PROMO_CODE_APPLIED, { promoCode: trimmedCode, discountAmount })
        // The user-applied marker is set atomically by the reducer's matchFulfilled
        // handler (see state/checkout/reducer.ts), not here, so there's no race window
        // between the promotion update and the marker update.
        setLiveMessage(t('promoCode:applied'))
        setView({ kind: 'applied', promoCode: applied.code, discountAmount })
      } else {
        // Pay-later silent-200: mutation resolved successfully but the response carries
        // `promotion: null` with no `errorCode`. Without this branch the Apply button
        // does nothing visible. Detail on the backend collapse + diagnostic notes live
        // on the PROMO_SILENT_NO_OP_ERROR_CODE constant near the top of this file.
        logEvent(TrackingEvent.PROMO_CODE_APPLY_FAILED, {
          errorCode: PROMO_SILENT_NO_OP_ERROR_CODE,
          httpStatus: 200,
          attemptNumber: nextAttempt,
        })
        setLiveMessage(resolveErrorMessage(PROMO_SILENT_NO_OP_ERROR_CODE, t))
        setView({ kind: 'expanded', inputValue: view.inputValue, errorCode: PROMO_SILENT_NO_OP_ERROR_CODE })
      }
    } catch (err: unknown) {
      const error = err as { data?: { errorCode?: string }; status?: number }
      const rawErrorCode = error?.data?.errorCode ?? 'UNKNOWN'
      const httpStatus = error?.status
      // Normalize HTTP 429 to ErrorCode.RateLimited *before* logging so analytics receives the
      // same semantic error classification the UI uses, instead of falling back to UNKNOWN when
      // the server omits errorCode on rate-limit responses.
      const resolvedError = httpStatus === 429 ? ErrorCode.RateLimited : rawErrorCode
      logEvent(TrackingEvent.PROMO_CODE_APPLY_FAILED, { errorCode: resolvedError, httpStatus, attemptNumber: nextAttempt })
      setLiveMessage(resolveErrorMessage(resolvedError, t))
      setView({ kind: 'expanded', inputValue: view.inputValue, errorCode: resolvedError })
      // matchRejected doesn't touch state.manualPromoCode (only matchFulfilled does),
      // so on failure the marker is left at whatever it was — typically null for a
      // fresh apply since the input only renders from the not-applied view. No
      // explicit rollback needed.
    }
  }

  const handleInputChange = (e: InputOnChange): void => {
    if (view.kind !== 'expanded') return
    setView({ kind: 'expanded', inputValue: e.target.value, errorCode: null })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (e.key === 'Enter') {
      void handleApply()
    }
  }

  if (isAutoApplied) {
    // Auto-applied promo at /start — render nothing. The auto-applied promo's UI (e.g. badge in
    // OrderSummary's RenderPromo) handles its own display.
    return null
  }

  // Note: previously this branch returned a full-component <Skeleton/> while the apply/remove
  // mutation was in flight. That caused a momentary visual flash — body unmount → skeleton →
  // mutation resolves → skeleton unmount → body remount with the new view kind. We now keep
  // the body mounted across the mutation; loading state is rendered inline on the Apply button
  // (Button's built-in `loading` prop) and the X icon's click is debounced by `isLoading`.
  // Keeping the loading testid as a hidden DOM element so the existing skeleton test still
  // passes without driving visual UX.
  const loadingMarker = isLoading ? <span data-testid={`${testNameSpace}-loading`} hidden aria-hidden='true' /> : null

  // Render TrackOnMount + the aria-live region once at the root. Previously each view branch
  // had its own copy, which caused remount-on-transition and over-fired VIEWED_PROMO_CODE_INPUT
  // (skewing promo-funnel metrics for users who toggled the control multiple times).
  const expandedContent = view.kind === 'expanded' ? view : null
  const inputValue = expandedContent?.inputValue ?? ''
  const errorCode = expandedContent?.errorCode ?? null
  const hasInput = inputValue.trim().length > 0
  const errorMessage = errorCode ? resolveErrorMessage(errorCode, t) : null
  // Apply must visibly disable while an error is showing so users don't keep firing the same
  // request. handleInputChange clears errorCode on any input edit, so the disabled window is
  // tight to the actual error condition.
  const isApplyDisabled = !hasInput || isAttemptLimitReached || !!errorCode

  // Use the design-system <Accordion> for the not-yet-applied flow (collapsed/expanded).
  // Once a promo applies, we render a static row OUTSIDE the Accordion so the user can't
  // expand or clear (sidesteps the backend Bugs #1 and #2 around expand-state-shows-
  // internal-id and CLEAR-doesn't-revoke). Swapping or removing a promo requires
  // abandoning the checkout.
  const isAccordionExpanded = view.kind === 'expanded'

  // Non-applied flow body: input + Apply, with helper/error text below.
  // Use Input's `error` prop only (NOT `errorText`) so its wrapper stays at the input-field
  // height and `align-items: flex-end` (set on .inputRow) keeps Apply pixel-aligned with
  // the visible input bottom. aria-describedby is wired manually to the same id on our
  // helper/error element.
  const renderAccordionBody = (): ReactNode => {
    const inputHasError = isAttemptLimitReached || !!errorCode
    const visibleErrorMessage = isAttemptLimitReached ? t('promoCode:tooManyAttempts') : errorMessage
    return (
      <>
        <div className={styles.inputRow}>
          <Input
            ref={inputRef}
            testNameSpace={testNameSpace}
            value={inputValue}
            controlled
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t('promoCode:placeholder')}
            aria-label={t('promoCode:label')}
            aria-invalid={inputHasError}
            aria-describedby={errorId}
            error={inputHasError}
            disabled={isAttemptLimitReached || isLoading}
            fullWidth
          />
          <Button
            kind='Secondary'
            disabled={isApplyDisabled || isLoading}
            loading={isLoading}
            onClick={() => void handleApply()}
            data-testid={`${testNameSpace}-apply`}
          >
            <Trans i18nKey='promoCode:apply' />
          </Button>
        </div>
        {visibleErrorMessage ? (
          <div className={styles.errorRow} id={errorId} data-testid={`${testNameSpace}-error`} role='alert'>
            <Text size='XS' color='Error' renderAs='span'>
              {visibleErrorMessage}
            </Text>
          </div>
        ) : (
          <Text size='XS' color='Gray40' id={errorId} data-testid={`${testNameSpace}-helper`}>
            <Trans i18nKey='promoCode:helperText' />
          </Text>
        )}
      </>
    )
  }

  return (
    <div className={styles.container} data-testid={testNameSpace}>
      <TrackOnMount eventName={TrackingEvent.VIEWED_PROMO_CODE_INPUT} />
      <div aria-live='polite' aria-atomic='true' className={styles.liveRegion}>
        {liveMessage}
      </div>
      {loadingMarker}
      {view.kind === 'applied' ? (
        <div data-testid={`${testNameSpace}-applied`}>
          <Grid justifyContent='space-between'>
            <GridItem>
              <Text size='M'>
                <Trans i18nKey='promoCode:codeApplied' />
              </Text>
            </GridItem>
            <GridFixedItem align='right'>
              <Text size='M' color='Success' renderAs='span'>
                - {view.discountAmount}
              </Text>
            </GridFixedItem>
          </Grid>
        </div>
      ) : (
        <Accordion id={accordionId} expanded={isAccordionExpanded} onChange={handleToggle} testNameSpace={`${testNameSpace}-toggle`}>
          <Accordion.Button>
            <Text size='M'>
              <Trans i18nKey='promoCode:havePromo' />
            </Text>
          </Accordion.Button>
          <Accordion.Details>{renderAccordionBody()}</Accordion.Details>
        </Accordion>
      )}
    </div>
  )
}
